import { DefaultValues } from 'react-hook-form';

import { PROPERTY_STATE_FIELD_CONFIGS } from '@hcs/property-state';
import {
  MlsState,
  MlsStateGroup,
  PropertyStateArgs,
  PropertyStateFields,
  PropertyStateFlat,
  PropertyTypeEnum,
  SpatialFiltersInput,
  SpatialSortPropertiesIdentifierInput,
} from '@hcs/types';
import { DateRange } from '@hcs/types';
import { nullToUndefined } from '@hcs/utils';
import {
  dateStrToMonthsAgo,
  dateToISOString,
  getTimeZone,
  monthsAgoToDateStr,
} from '@hcs/utils';

import { FetchBuyBoxActivityArg } from '../api';
import {
  LEAD_FEED_PROPERTY_TYPE,
  TIME_SERIES_RESOLUTION,
} from '../constants/buyBox.constants';
import {
  BuyBox,
  BuyBoxFormData,
  BuyBoxZipcode,
  ExcludeFilterOrNull,
  LeadFeedPropertyType,
  RangeFilters,
  SaleDateSentinel,
} from '../types';

export class BuyBoxBulkEditError extends Error {
  // base constructor only accepts string message as an argument
  // we extend it here to accept an object, allowing us to pass other data
  constructor(
    public buyBoxId: BuyBox['id'],
    public field: keyof RangeFilters,
    message: string,
  ) {
    super(message);
    // Set the prototype explicitly.
    // Object.setPrototypeOf(this, BuyBoxBulkEditError.prototype);
    this.name = 'BulkEditBuyBoxError';
    this.buyBoxId = buyBoxId; // this property is defined in parent
    this.field = field;
  }
}

export const getIsExcludedFromexcludeFilter = <ExcludeValueType>(
  excludeFilter: ExcludeFilterOrNull<ExcludeValueType>,
): boolean => {
  if (excludeFilter === null) {
    return false;
  }
  // if the exclude filter is not null, we should assume the exclusion is applied.
  return true;
};

export const getExcludedZipcodesInput = (
  unSelectedZips?: BuyBoxZipcode[],
): SpatialSortPropertiesIdentifierInput['excludeZipcodes'] => {
  if (!unSelectedZips || unSelectedZips.length === 0) {
    return undefined;
  }
  return { zipcodes: unSelectedZips };
};

const LeadFeedToSpatialPropertyTypeMap = {
  [LEAD_FEED_PROPERTY_TYPE.sfr]: PropertyTypeEnum.Sfr,
  [LEAD_FEED_PROPERTY_TYPE.condo]: PropertyTypeEnum.Condo,
  [LEAD_FEED_PROPERTY_TYPE.manufactured]: PropertyTypeEnum.Manufactured,
  [LEAD_FEED_PROPERTY_TYPE.multi]: PropertyTypeEnum.MultiFamily,
  [LEAD_FEED_PROPERTY_TYPE.townhouse]: PropertyTypeEnum.Townhouse,
  [LEAD_FEED_PROPERTY_TYPE.coop]: PropertyTypeEnum.Condo,
};

export const getPropertyTypeCerberusFromData = (
  propertyTypes: LeadFeedPropertyType[],
): PropertyTypeEnum[] | undefined => {
  return propertyTypes.reduce<PropertyTypeEnum[]>((accum, leadFeedPT) => {
    // coop is under the umbrella of condo in cerberus
    if (
      leadFeedPT === LEAD_FEED_PROPERTY_TYPE.coop ||
      leadFeedPT === LEAD_FEED_PROPERTY_TYPE.condo
    ) {
      // add condo if not already included
      if (accum.indexOf(PropertyTypeEnum.Condo) === -1) {
        accum.push(PropertyTypeEnum.Condo);
      }
    } else {
      accum.push(LeadFeedToSpatialPropertyTypeMap[leadFeedPT]);
    }
    return accum;
  }, []);
};

const SPATIAL_TO_LEAD_FEED_PROPERTY_TYPES = {
  [PropertyTypeEnum.Sfr]: LEAD_FEED_PROPERTY_TYPE.sfr,
  [PropertyTypeEnum.Condo]: LEAD_FEED_PROPERTY_TYPE.condo,
  [PropertyTypeEnum.Manufactured]: LEAD_FEED_PROPERTY_TYPE.manufactured,
  [PropertyTypeEnum.MultiFamily]: LEAD_FEED_PROPERTY_TYPE.multi,
  [PropertyTypeEnum.Townhouse]: LEAD_FEED_PROPERTY_TYPE.townhouse,
};

export const getPropertyTypeLeadFeedFromCerberus = (
  propertyTypes: PropertyTypeEnum[] | undefined,
): LeadFeedPropertyType[] | undefined => {
  if (!propertyTypes) {
    return undefined;
  }
  const result: LeadFeedPropertyType[] = [];
  Object.entries(SPATIAL_TO_LEAD_FEED_PROPERTY_TYPES).forEach(
    ([key, value]) => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const typedKey = key as keyof typeof SPATIAL_TO_LEAD_FEED_PROPERTY_TYPES;
      if (propertyTypes.includes(typedKey)) {
        result.push(value);
      }
    },
  );
  return result;
};

const FALSE_OR_NULL_SPATIAL_BOOL: [boolean, null] = [false, null];

const SENTINEL_TO_MONTHS: Record<SaleDateSentinel, number> = {
  [SaleDateSentinel.Prior3Months]: 3,
  [SaleDateSentinel.Prior6Months]: 6,
  [SaleDateSentinel.Prior9Months]: 9,
  [SaleDateSentinel.PriorYear]: 12,
  [SaleDateSentinel.Prior2Years]: 24,
};

const MONTHS_TO_SENTINEL: Record<number, SaleDateSentinel> = {
  3: SaleDateSentinel.Prior3Months,
  6: SaleDateSentinel.Prior6Months,
  9: SaleDateSentinel.Prior9Months,
  12: SaleDateSentinel.PriorYear,
  24: SaleDateSentinel.Prior2Years,
};

const SENTINEL_MONTHS_ORDERED = [3, 6, 9, 12, 24] as const;

/** Converts an approximate months-ago value to the nearest SaleDateSentinel. */
const monthsToNearestSentinel = (months: number): SaleDateSentinel => {
  const nearest = SENTINEL_MONTHS_ORDERED.reduce((a, b) =>
    Math.abs(b - months) < Math.abs(a - months) ? b : a,
  );
  return MONTHS_TO_SENTINEL[nearest];
};

/** Removes all 'undefined' values from an array of mixed types, e.g. (sometype | undefined)[] */
function compactArray<T>(arr: unknown): T[] {
  if (!Array.isArray(arr)) return [];
  return arr.filter((v): v is T => v !== undefined);
}

/**
 * Unwraps an InputMaybe array: handles null/undefined outer value and strips
 * null/undefined from each element, returning a concrete typed array.
 */
function filterInputMaybe<T>(
  arr: Array<T | null | undefined> | null | undefined,
): T[] {
  if (!arr) return [];
  return arr.filter((v): v is T => v != null);
}

export const getSpatialFiltersInput = (
  buyBoxData: DefaultValues<BuyBoxFormData>,
): SpatialFiltersInput => {
  return {
    mlsState: [MlsState.Active, MlsState.ComingSoon],
    minListPrice: nullToUndefined(buyBoxData.listingPriceMin),
    maxListPrice: nullToUndefined(buyBoxData.listingPriceMax),
    minBathsWithHalfs: nullToUndefined(buyBoxData.bathsMin),
    maxBathsWithHalfs: nullToUndefined(buyBoxData.bathsMax),
    minBeds: nullToUndefined(buyBoxData.bedsMin),
    maxBeds: nullToUndefined(buyBoxData.bedsMax),
    minLotArea: nullToUndefined(buyBoxData.lotSizeMin),
    maxLotArea: nullToUndefined(buyBoxData.lotSizeMax),
    minStories: nullToUndefined(buyBoxData.numStoriesMin),
    maxStories: nullToUndefined(buyBoxData.numStoriesMax),
    minSqft: nullToUndefined(buyBoxData.glaMin),
    maxSqft: nullToUndefined(buyBoxData.glaMax),
    minYearBuilt: nullToUndefined(buyBoxData.yearBuiltMin),
    maxYearBuilt: nullToUndefined(buyBoxData.yearBuiltMax),
    minParkingSpots: nullToUndefined(buyBoxData.garageSpacesMin),
    maxParkingSpots: nullToUndefined(buyBoxData.garageSpacesMax),
    pool: buyBoxData.excludePool ? FALSE_OR_NULL_SPATIAL_BOOL : undefined,
    privateSewer: buyBoxData.excludeSeptic
      ? FALSE_OR_NULL_SPATIAL_BOOL
      : undefined,
    propertyTypeEnum: (() => {
      const selectedTypes = compactArray<LeadFeedPropertyType>(
        buyBoxData.propertyType,
      );
      // All types selected is semantically equivalent to no filter — return
      // undefined so the UI does not count it as an active filter.
      if (
        selectedTypes.length >= Object.values(LEAD_FEED_PROPERTY_TYPE).length
      ) {
        return undefined;
      }
      return selectedTypes.length > 0
        ? getPropertyTypeCerberusFromData(selectedTypes)
        : undefined;
    })(),
    minCrimeCountyPercentile: nullToUndefined(buyBoxData.crimePercentileMin),
    maxCrimeCountyPercentile: nullToUndefined(buyBoxData.crimePercentileMax),
    minBestPrimarySchoolPercentile: nullToUndefined(
      buyBoxData.primarySchoolPercentileMin,
    ),
    maxBestPrimarySchoolPercentile: nullToUndefined(
      buyBoxData.primarySchoolPercentileMax,
    ),
    minBestMiddleSchoolPercentile: nullToUndefined(
      buyBoxData.middleSchoolPercentileMin,
    ),
    maxBestMiddleSchoolPercentile: nullToUndefined(
      buyBoxData.middleSchoolPercentileMax,
    ),
    minBestHighSchoolPercentile: nullToUndefined(
      buyBoxData.highSchoolPercentileMin,
    ),
    maxBestHighSchoolPercentile: nullToUndefined(
      buyBoxData.highSchoolPercentileMax,
    ),
    minSalePrice: nullToUndefined(buyBoxData.salePriceMin),
    maxSalePrice: nullToUndefined(buyBoxData.salePriceMax),
    minRentalYield:
      buyBoxData.rentalYieldMin != null
        ? buyBoxData.rentalYieldMin / 100
        : undefined,
    maxRentalYield:
      buyBoxData.rentalYieldMax != null
        ? buyBoxData.rentalYieldMax / 100
        : undefined,
    minSaleDate:
      buyBoxData.saleDate != null
        ? (monthsAgoToDateStr(SENTINEL_TO_MONTHS[buyBoxData.saleDate]) ??
          undefined)
        : undefined,
    conditionClass:
      buyBoxData.conditionClass != null
        ? [buyBoxData.conditionClass]
        : undefined,
    basement: buyBoxData.hasBasement ?? undefined,
    mlsStateGroup: compactArray<MlsStateGroup>(buyBoxData.listingStatus).length
      ? compactArray<MlsStateGroup>(buyBoxData.listingStatus)
      : undefined,
  };
};

const calcDiff = (psValue: number | null | undefined, diff: number) => {
  if (psValue != null) {
    const value =
      diff % 1 ? Math.round(psValue + psValue * diff) : psValue + diff;
    console.log('calcDiff', psValue, diff, ' = ', value);
    return value;
  }
};

const getValueFromPropertyState = <F extends PropertyStateFields>(
  propertyStateArgs: PropertyStateArgs,
  propertyStateField: F,
): PropertyStateFlat[F] | null => {
  const { getValue } = PROPERTY_STATE_FIELD_CONFIGS[propertyStateField];
  const psValue = getValue(propertyStateArgs);
  console.log('getValueFromPropertyState', propertyStateField, psValue);
  return psValue || null;
};

export const getBuyBoxFiltersFromPropertyState = (
  propertyStateArgs: PropertyStateArgs,
): Partial<BuyBoxFormData> => {
  const propertyType = getValueFromPropertyState(
    propertyStateArgs,
    PropertyStateFields.propertyType,
  );
  return {
    listingPriceMin: calcDiff(
      getValueFromPropertyState(
        propertyStateArgs,
        PropertyStateFields.currentPrice,
      ),
      -0.2,
    ),
    listingPriceMax: calcDiff(
      getValueFromPropertyState(
        propertyStateArgs,
        PropertyStateFields.currentPrice,
      ),
      0.2,
    ),
    bathsMin: calcDiff(
      getValueFromPropertyState(
        propertyStateArgs,
        PropertyStateFields.bathrooms,
      ),
      -1,
    ),
    bathsMax: calcDiff(
      getValueFromPropertyState(
        propertyStateArgs,
        PropertyStateFields.bathrooms,
      ),
      1,
    ),
    bedsMin: calcDiff(
      getValueFromPropertyState(
        propertyStateArgs,
        PropertyStateFields.bedrooms,
      ),
      -1,
    ),
    bedsMax: calcDiff(
      getValueFromPropertyState(
        propertyStateArgs,
        PropertyStateFields.bedrooms,
      ),
      1,
    ),
    lotSizeMin: calcDiff(
      getValueFromPropertyState(propertyStateArgs, PropertyStateFields.lotSize),
      -0.25,
    ),
    lotSizeMax: calcDiff(
      getValueFromPropertyState(propertyStateArgs, PropertyStateFields.lotSize),
      0.25,
    ),
    numStoriesMin: calcDiff(
      getValueFromPropertyState(propertyStateArgs, PropertyStateFields.stories),
      -1,
    ),
    numStoriesMax: calcDiff(
      getValueFromPropertyState(propertyStateArgs, PropertyStateFields.stories),
      1,
    ),
    glaMin: calcDiff(
      getValueFromPropertyState(
        propertyStateArgs,
        PropertyStateFields.livingArea,
      ),
      -0.2,
    ),
    glaMax: calcDiff(
      getValueFromPropertyState(
        propertyStateArgs,
        PropertyStateFields.livingArea,
      ),
      0.2,
    ),
    yearBuiltMin: calcDiff(
      getValueFromPropertyState(
        propertyStateArgs,
        PropertyStateFields.yearBuilt,
      ),
      -0.01,
    ),
    yearBuiltMax: calcDiff(
      getValueFromPropertyState(
        propertyStateArgs,
        PropertyStateFields.yearBuilt,
      ),
      0.01,
    ),
    excludePool:
      getValueFromPropertyState(propertyStateArgs, PropertyStateFields.pool) ||
      undefined,
    propertyType: propertyType
      ? getPropertyTypeLeadFeedFromCerberus([propertyType])
      : undefined,
  };
};

export const getBuyBoxFiltersFromSpatial = (
  spatialFiltersInput: SpatialFiltersInput,
): Partial<BuyBoxFormData> => {
  return {
    listingPriceMin: spatialFiltersInput.minListPrice || null,
    listingPriceMax: spatialFiltersInput.maxListPrice || null,
    bathsMin:
      spatialFiltersInput.minBathsWithHalfs ??
      spatialFiltersInput.minBaths ??
      null,
    bathsMax:
      spatialFiltersInput.maxBathsWithHalfs ??
      spatialFiltersInput.maxBaths ??
      null,
    bedsMin: spatialFiltersInput.minBeds || null,
    bedsMax: spatialFiltersInput.maxBeds || null,
    lotSizeMin: spatialFiltersInput.minLotArea || null,
    lotSizeMax: spatialFiltersInput.maxLotArea || null,
    numStoriesMin: spatialFiltersInput.minStories || null,
    numStoriesMax: spatialFiltersInput.maxStories || null,
    glaMin: spatialFiltersInput.minSqft || null,
    glaMax: spatialFiltersInput.maxSqft || null,
    yearBuiltMin: spatialFiltersInput.minYearBuilt || null,
    yearBuiltMax: spatialFiltersInput.maxYearBuilt || null,
    excludePool: spatialFiltersInput.pool === false ? true : undefined,
    garageSpacesMax: spatialFiltersInput.maxParkingSpots || null,
    garageSpacesMin: spatialFiltersInput.minParkingSpots || null,
    propertyType: getPropertyTypeLeadFeedFromCerberus(
      Array.isArray(spatialFiltersInput.propertyTypeEnum)
        ? spatialFiltersInput.propertyTypeEnum.filter((e) => !!e)
        : spatialFiltersInput.propertyTypeEnum || undefined,
    ),
    crimePercentileMin: spatialFiltersInput.minCrimeCountyPercentile ?? null,
    crimePercentileMax: spatialFiltersInput.maxCrimeCountyPercentile ?? null,
    primarySchoolPercentileMin:
      spatialFiltersInput.minBestPrimarySchoolPercentile ?? null,
    primarySchoolPercentileMax:
      spatialFiltersInput.maxBestPrimarySchoolPercentile ?? null,
    middleSchoolPercentileMin:
      spatialFiltersInput.minBestMiddleSchoolPercentile ?? null,
    middleSchoolPercentileMax:
      spatialFiltersInput.maxBestMiddleSchoolPercentile ?? null,
    highSchoolPercentileMin:
      spatialFiltersInput.minBestHighSchoolPercentile ?? null,
    highSchoolPercentileMax:
      spatialFiltersInput.maxBestHighSchoolPercentile ?? null,
    salePriceMin: spatialFiltersInput.minSalePrice ?? null,
    salePriceMax: spatialFiltersInput.maxSalePrice ?? null,
    rentalYieldMin:
      spatialFiltersInput.minRentalYield != null
        ? spatialFiltersInput.minRentalYield * 100
        : null,
    rentalYieldMax:
      spatialFiltersInput.maxRentalYield != null
        ? spatialFiltersInput.maxRentalYield * 100
        : null,
    saleDate: spatialFiltersInput.minSaleDate
      ? monthsToNearestSentinel(
          dateStrToMonthsAgo(spatialFiltersInput.minSaleDate),
        )
      : null,
    conditionClass: spatialFiltersInput.conditionClass?.[0] ?? null,
    hasBasement:
      typeof spatialFiltersInput.basement === 'boolean'
        ? spatialFiltersInput.basement
        : null,
    listingStatus: filterInputMaybe(spatialFiltersInput.mlsStateGroup),
  };
};

export const getCommonIdParams = (
  buyBoxData: BuyBoxFormData,
): SpatialSortPropertiesIdentifierInput | null => {
  // Use placeId for City/Zipcode boundaries, otherwise use MSA
  if (buyBoxData.customZipcodes.length > 0) {
    return {
      zipcode: {
        zipcodes: buyBoxData.customZipcodes,
      },
    };
  }

  if (buyBoxData.msaId) {
    return {
      msa: {
        msaIds: [`${buyBoxData.msaId}`],
      },
      excludeZipcodes: getExcludedZipcodesInput(buyBoxData.unSelectedZipcodes),
    };
  }

  // No valid ID params - return null to disable query
  return null;
};

export const dateRangeToFetchBuyBoxActivityArg = (
  dateRange: DateRange | null,
): FetchBuyBoxActivityArg | null => {
  if (dateRange === null) {
    return null;
  }
  return {
    startDate: dateToISOString(dateRange.startDate),
    endDate: dateToISOString(dateRange.endDate),
    timeZone: getTimeZone(),
    resolution: TIME_SERIES_RESOLUTION.Day,
  };
};
